"use client";

import Link from "next/link";
import { Button } from "@/components/ui/button";
import {
  Monitor,
  Shield,
  Sparkles,
  Users,
  Clock,
  FileText,
  QrCode,
  Smartphone,
} from "lucide-react";
import { useState, useEffect } from "react";

export default function Home() {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  if (!mounted) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 relative overflow-hidden">
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-purple-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-cyan-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-2000"></div>
        <div className="absolute top-40 left-40 w-80 h-80 bg-pink-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-4000"></div>
      </div>

      {/* Grid pattern overlay */}
      <div className="absolute inset-0 opacity-40">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%239C92AC' fill-opacity='0.05'%3E%3Ccircle cx='30' cy='30' r='1'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}
        ></div>
      </div>

      <div className="relative z-10 min-h-screen flex flex-col">
        {/* Header */}
        <div className="text-center pt-16 pb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-cyan-400 to-purple-500 rounded-2xl mb-6 shadow-2xl animate-pulse-slow">
            <Sparkles className="h-10 w-10 text-white" />
          </div>
          <h1 className="text-6xl md:text-7xl font-bold bg-gradient-to-r from-cyan-400 via-purple-400 to-pink-400 bg-clip-text text-transparent mb-4 animate-fade-in">
            KidCheck
          </h1>
          <p className="text-xl md:text-2xl text-slate-300 max-w-3xl mx-auto px-4 animate-fade-in-delay">
            Modern child check-in system for the digital age
          </p>
        </div>

        {/* Main Action Cards */}
        <div className="flex-1 flex items-center justify-center px-4 py-8">
          <div className="grid md:grid-cols-2 gap-8 max-w-6xl w-full">
            {/* Kiosk Mode Card */}
            <div className="group relative">
              <div className="absolute -inset-1 bg-gradient-to-r from-cyan-400 to-blue-500 rounded-3xl blur opacity-25 group-hover:opacity-75 transition duration-1000 group-hover:duration-200"></div>
              <Link href="/kiosk" className="block relative">
                <div className="bg-slate-800/80 backdrop-blur-xl border border-slate-700/50 rounded-3xl p-8 md:p-12 h-full hover:bg-slate-800/90 transition-all duration-500 group-hover:scale-105 group-hover:shadow-2xl">
                  <div className="text-center space-y-6">
                    <div className="inline-flex items-center justify-center w-24 h-24 bg-gradient-to-br from-cyan-400 to-blue-500 rounded-2xl shadow-xl group-hover:shadow-cyan-500/25 transition-all duration-500 group-hover:rotate-6">
                      <Monitor className="h-12 w-12 text-white" />
                    </div>

                    <div className="space-y-4">
                      <h2 className="text-3xl md:text-4xl font-bold text-white group-hover:text-cyan-300 transition-colors duration-300">
                        Kiosk Mode
                      </h2>
                      <p className="text-slate-300 text-lg leading-relaxed">
                        Touch-friendly interface for quick and easy child
                        check-ins and check-outs
                      </p>
                    </div>

                    <div className="space-y-3 text-left">
                      <div className="flex items-center text-slate-400 group-hover:text-slate-300 transition-colors">
                        <QrCode className="h-5 w-5 mr-3 text-cyan-400" />
                        QR Code & PIN Access
                      </div>
                      <div className="flex items-center text-slate-400 group-hover:text-slate-300 transition-colors">
                        <Smartphone className="h-5 w-5 mr-3 text-cyan-400" />
                        Tablet Optimized
                      </div>
                      <div className="flex items-center text-slate-400 group-hover:text-slate-300 transition-colors">
                        <Clock className="h-5 w-5 mr-3 text-cyan-400" />
                        Real-time Updates
                      </div>
                    </div>

                    <Button
                      size="lg"
                      className="w-full h-14 bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white font-semibold text-lg rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 group-hover:scale-105"
                    >
                      Launch Kiosk
                      <Monitor className="ml-2 h-5 w-5" />
                    </Button>
                  </div>
                </div>
              </Link>
            </div>

            {/* Staff Login Card */}
            <div className="group relative">
              <div className="absolute -inset-1 bg-gradient-to-r from-purple-400 to-pink-500 rounded-3xl blur opacity-25 group-hover:opacity-75 transition duration-1000 group-hover:duration-200"></div>
              <Link href="/login" className="block relative">
                <div className="bg-slate-800/80 backdrop-blur-xl border border-slate-700/50 rounded-3xl p-8 md:p-12 h-full hover:bg-slate-800/90 transition-all duration-500 group-hover:scale-105 group-hover:shadow-2xl">
                  <div className="text-center space-y-6">
                    <div className="inline-flex items-center justify-center w-24 h-24 bg-gradient-to-br from-purple-400 to-pink-500 rounded-2xl shadow-xl group-hover:shadow-purple-500/25 transition-all duration-500 group-hover:rotate-6">
                      <Shield className="h-12 w-12 text-white" />
                    </div>

                    <div className="space-y-4">
                      <h2 className="text-3xl md:text-4xl font-bold text-white group-hover:text-purple-300 transition-colors duration-300">
                        Login
                      </h2>
                      <p className="text-slate-300 text-lg leading-relaxed">
                        Secure access for administrators and guardians to manage
                        accounts and view reports
                      </p>
                    </div>

                    <div className="space-y-3 text-left">
                      <div className="flex items-center text-slate-400 group-hover:text-slate-300 transition-colors">
                        <Users className="h-5 w-5 mr-3 text-purple-400" />
                        Admin & Guardian Access
                      </div>
                      <div className="flex items-center text-slate-400 group-hover:text-slate-300 transition-colors">
                        <FileText className="h-5 w-5 mr-3 text-purple-400" />
                        Reports & Analytics
                      </div>
                      <div className="flex items-center text-slate-400 group-hover:text-slate-300 transition-colors">
                        <Shield className="h-5 w-5 mr-3 text-purple-400" />
                        Secure Authentication
                      </div>
                    </div>

                    <Button
                      size="lg"
                      className="w-full h-14 bg-gradient-to-r from-purple-500 to-pink-600 hover:from-purple-400 hover:to-pink-500 text-white font-semibold text-lg rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 group-hover:scale-105"
                    >
                      Staff Portal
                      <Shield className="ml-2 h-5 w-5" />
                    </Button>
                  </div>
                </div>
              </Link>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center pb-8 px-4">
          <p className="text-slate-400 text-sm">
            Trusted by childcare facilities worldwide • Secure • Fast • Reliable
          </p>
        </div>
      </div>
    </div>
  );
}
